import { Fragment, useEffect, useRef, useState } from 'react';

import { Form, Link, useFetcher, useNavigation, useParams } from 'react-router';

import { Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import type { ProductCardFragment } from '@boc/framework/saleor-graphql';

import { TwPicture } from '@boc/images';

import { imgUrl, urls } from '~/urls';

import { brandInfo } from '#/brand';

import { loader } from '../../routes/search';

export const SearchSidebar = ({
  show,
  setShow,
  bestsellers,
}: {
  show: boolean;
  setShow: (show: boolean) => void;
  bestsellers?: ProductCardFragment[];
}) => {
  const fetcher = useFetcher<typeof loader>({ key: 'global-product-search' });

  const params = useParams();

  const refForm = useRef<HTMLFormElement | null>(null);

  const navigation = useNavigation();

  const [searchProducts, setSearchProducts] = useState<ProductCardFragment[] | null>(null);

  useEffect(() => {
    if (fetcher.state == 'loading') {
      setSearchProducts(null);
    }

    if (fetcher.state == 'idle' && fetcher.data) {
      setSearchProducts(fetcher.data.results || []);
    }
  }, [fetcher]);

  useEffect(() => {
    if (navigation.state == 'loading') {
      setShow(false);
    }
  }, [navigation, setShow]);

  const heading = searchProducts == null ? 'Popular Products' : `Top Products`;
  const products = searchProducts == null ? bestsellers : searchProducts;

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setShow}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="bg-opacity-25 bg-primary-2/30 fixed inset-0 backdrop-blur-xs" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 flex">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enterFrom="lg:translate-x-full lg:translate-y-0 translate-y-full"
            enterTo="lg:translate-x-0 translate-y-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leaveFrom="lg:translate-x-0 translate-y-0"
            leaveTo="lg:translate-x-full lg:translate-y-0 translate-y-full"
          >
            <Dialog.Panel className="relative ml-auto mt-auto flex h-4/5 w-full flex-col overflow-y-auto bg-primary-2-10 p-10 lg:h-full lg:max-w-md">
              <div className="space-y-3 text-primary-2-80">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading-condensed text-lg font-medium leading-6 tracking-tight">
                    Search {brandInfo.name}
                  </h2>
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center p-2"
                    onClick={() => setShow(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="w-full border-b border-primary-2-20" />
                <div>
                  <Form
                    method="get"
                    action={urls.products.index(params)}
                    onChange={(event) => {
                      fetcher.submit(event.currentTarget);
                    }}
                    ref={refForm}
                  >
                    <div className="relative border border-primary-3-20">
                      <input type="text" name="search" className="w-full" />
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <MagnifyingGlassIcon className="th-5 w-5" aria-hidden="true" />
                      </div>
                    </div>
                  </Form>
                </div>
                <div className="space-y-8 pt-10">
                  <div className="">{heading}</div>
                  <div className="flex flex-col space-y-3">
                    {products?.map((product) => {
                      return (
                        <Link
                          key={product.id}
                          to={urls.products.details(params, { slug: product.slug })}
                        >
                          <div className="flex items-center gap-6">
                            <div className="h-20 w-20 bg-primary-2-20 p-1 flex items-center justify-center overflow-hidden rounded flex-shrink-0">
                              {product.thumbnail?.url && (
                                <TwPicture
                                  imgUrl={imgUrl}
                                  src={product.thumbnail?.url}
                                  defaultSize={80}
                                  alt={product.thumbnail?.alt || product.name}
                                  className="h-full w-full object-cover mix-blend-multiply"
                                />
                              )}
                            </div>
                            <div className="">
                              <div className="">{product.name}</div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
